// FrameX Step 2: run the Step-1 detector on one sheet and place each surviving
// box as a frame — an item already carrying its clip image + a location
// carrying the clip, applied together so a frame is never clipless
// (Document.svelte only shows edit affordances when `loc.clip` exists).
// Idempotent: dedupeAgainstExisting drops boxes overlapping an existing frame,
// so re-running adds only what's new.

import { createItem, createLocation } from "@local/lamina-core";
import pageImage from "#src/extensions/page-image.js";
import { downscaleToB64 } from "../inference/image.js";
import {
  detectOpenings,
  parseDetections,
  DETECTOR_MAX_EDGE_PX,
} from "../inference/tasks/detect-openings.js";
import { boxToPageClip, dedupeAgainstExisting } from "./geometry.js";
import { sheetFrames } from "./derive.js";
import { setPageFlags } from "./page-flags.js";
import { drawingForClip } from "#src/extensions/docs/location-drawing.js";

/**
 * Run the locator on one sheet and place a frame for every surviving
 * detection. `group` is the store (setPageFlags reads it via `get()` to
 * resolve the document as it stands *after* the detector returns); `groupId`
 * is passed separately since the store doesn't expose its id. `doc` is read
 * before the detector; anything written back afterwards goes through the store.
 * Returns the number of frames placed and their new location ids, so the
 * caller can select what the run just created.
 */
export async function locateFramesOnSheet({
  pageId,
  doc,
  group,
  groupId,
  user,
  org,
  collections,
  indexedLocations,
  token,
  viewer,
  visibleIndices,
}) {
  const page = doc.data.pages[pageId];
  if (!page) return { added: 0, locationIds: [] };

  const source = await pageImage({ name: page.name, page: page.page });
  const { base64 } = await downscaleToB64(
    `data:${source.media_type};base64,${source.data}`,
    DETECTOR_MAX_EDGE_PX,
  );

  let detections = null;
  for await (const event of detectOpenings({ imageB64: base64, token })) {
    if (event.event === "warming" || event.event === "running") continue;
    if (event.event === "error")
      throw new Error(event.message || "Detection failed.");
    if (event.event === "complete") detections = parseDetections(event);
  }
  if (detections === null)
    throw new Error("Detection stream ended without a result.");

  const index = visibleIndices?.[pageId];
  const tile = index != null ? viewer?.tile(index) : null;
  const size = tile?.getContentSize?.();
  if (!size) throw new Error("Sheet isn't loaded on canvas.");

  const candidates = detections
    .map((d) =>
      boxToPageClip(d.bbox_2d, {
        pageWidth: size.x,
        pageHeight: size.y,
        drawingScale: page.scale,
      }),
    )
    .filter(Boolean);

  const existingClips = sheetFrames(indexedLocations?.[pageId]).map(
    (l) => l.clip,
  );
  const survivors = dedupeAgainstExisting(candidates, existingClips);

  // Collected for the caller to select — declared out here so the empty case
  // still returns an array rather than undefined.
  const locationIds = [];

  if (survivors.length) {
    // Grows as items are created so each one's mark increments off the
    // last, instead of every frame in the batch landing on the same mark.
    const records = [...(collections || [])];
    const startIndex = (indexedLocations?.[pageId] || []).length;
    const actions = [];

    for (const [i, clip] of survivors.entries()) {
      const item = createItem(
        groupId,
        user?.id,
        records,
        [],
        {
          is_collection: true,
          replace_with_drawing: true,
          show_drawing_overlay: true,
        },
        { default_item_mark: org?.default_item_mark },
      );
      records.push(item);

      const location = createLocation({
        group_id: groupId,
        document_id: doc.id,
        index: startIndex + i,
        page_id: pageId,
        position: { x: clip.x + clip.width / 2, y: clip.y + clip.height / 2 },
        record_type: "collection",
        record_id: item.id,
        clip,
      });
      locationIds.push(location.id);

      // The clipped page image — what "Toggle Clip Image" applies — attached
      // before the item is inserted. Written as a second transaction it raced
      // its own insert: the item UPDATE could reach Postgres ahead of the
      // INSERT, match zero rows and return success, so the frame looked right
      // until the next reload.
      const drawing = await drawingForClip(location, doc);
      if (drawing) item.drawing = drawing;

      actions.push({
        type: "item",
        action: "add",
        records: [item],
        block: true,
      });
      actions.push({ type: "location", action: "add", records: [location] });
    }

    await group.update(actions);
  }

  setPageFlags(group, doc.id, [pageId], { detectDone: true });

  return { added: survivors.length, locationIds };
}
